import { Component, EventEmitter, forwardRef, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputDensityEnum } from '@app/shared/enums/form-field';
import { TranslateService } from '@ngx-translate/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { IListItemIcon, ISetSelectBoxIcon, listItemIcon, setSelectBoxIcon } from './constants';
import { IconExprEnum } from '@app/shared/interfaces/dropdown-option';

@Component({
  selector: 'cm-select-box',
  templateUrl: './select-box.component.html',
  styleUrls: [
    './select-box.component.scss',
    './select-box-outline.component.scss',
    './select-box-outline-extras.component.scss',
    '../../../../styles/components/_dropdown.scss',
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => SelectBoxComponent),
      multi: true,
    },
  ],
})
export class SelectBoxComponent implements ControlValueAccessor {
  constructor(private translateService: TranslateService) {}

  @ViewChild('targetSelectBox', { static: false })
  targetSelectBox: DxSelectBoxComponent;

  @Input() template: TemplateRef<any>;
  @Input() dataSource: any;
  @Input() useDefaultStyle = true;
  @Input() acceptCustomValue = true;
  @Input() searchEnabled: any;
  @Input() placeholder: string;
  @Input() displayExpr: any;
  @Input() matHeaderViewEnabled = false;
  @Input() valueExpr: any;
  @Input() showExpandedIcons: boolean;
  @Input() searchMode: any = 'contains';
  @Input() searchTimeout: string;
  @Input() minSearchLength: number;
  @Input() showDataBeforeSearch: string;
  @Input() searchExpr: any;
  @Input() showClearButton: string;
  @Input() selectedItem: any;
  @Input() isFormField = true;
  @Input() hideBadge: boolean;
  @Input() highlight: boolean;
  @Input() hideEmptyResult = false;
  @Input() selectOnLeave: boolean;
  @Input() disabled: boolean;
  @Input() allowNewValue: boolean;
  @Input() errors: any;
  @Input() errorMessage: string;
  @Input() errorLocation: string;
  @Input() required: boolean;
  @Input() density: InputDensityEnum;
  @Input() readonly = false;
  @Input() isLoading = false;
  @Input() dataCy: string;
  @Input() iconExpr: IconExprEnum;
  @Input() dropDownOptions?: { width?: string | number; height?: string | number; [key: string]: any };

  @Output() selectionChanged = new EventEmitter();
  @Output() valuesChanged = new EventEmitter();
  @Output() customItemCreating = new EventEmitter();
  @Output() focusedOut = new EventEmitter();
  @Output() keyChanged = new EventEmitter();
  // icon generation (needs the iconExpr)
  public listItemIcon: IListItemIcon = listItemIcon;
  public setSelectBoxIcon: ISetSelectBoxIcon = setSelectBoxIcon;

  public control: FormControl;
  public focusedIn = false;
  public touched = false;
  InputDensityEnum = InputDensityEnum;

  private _value: any;
  onChange: any = () => {};
  onTouch: any = () => {};

  get value(): any {
    return this._value;
  }

  set value(value: any) {
    this._value = value;
    this.onChange(value);
    this.targetSelectBox.instance.blur();
  }

  get selector(): any {
    return this.selectedItem;
  }

  set selector(value: any) {
    this.selectedItem = value;
  }

  writeValue(value: string): void {
    this._value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  focusOut($event) {
    this.touched = true;
    this.focusedIn = false;

    document.getElementById('search-icon')?.classList.remove('icon-active');
    document.getElementById('search-container').classList.remove('select-box-container-active');

    if (this.selectOnLeave) {
      const currentText = this.targetSelectBox.text;

      const foundItem: any = this.targetSelectBox.items.find(
        (v: any) => v[this.searchExpr].toLowerCase() === currentText?.toLowerCase()
      );

      if (foundItem) this.selectionChanged.emit({ selectedItem: foundItem });
    } else this.focusedOut.emit($event);
  }

  focusIn() {
    this.focusedIn = true;

    document.getElementById('search-icon')?.classList.add('icon-active');
    document.getElementById('search-container').classList.add('select-box-container-active');
  }

  valueChanged(fn: any) {
    this.valuesChanged.emit(fn);
    this.onChange(fn.value);
  }

  clearInput() {
    this.value = null;
  }

  createCustomItem(fn: any) {
    const searchExpression = { ...fn }.text;
    if (!this.acceptCustomValue || this.allowNewValue) {
      const foundItem: any = this.targetSelectBox.items.find(
        (v: any) => v[this.searchExpr].toLowerCase() === fn.text?.toLowerCase()
      );

      if (!foundItem) {
        fn.customItem = {};
        fn.text = '';
      }

      if (this.allowNewValue) {
        fn.text = searchExpression;
        fn.allowNewValue = true;
      }
    }

    this.customItemCreating.emit(fn);
  }

  keyDown(event: any): void {
    const currentText = this.targetSelectBox.text;
    if (event.event.key === 'Enter' || event.event.keyCode === 13) {
      const findItem: any = this.targetSelectBox.items.find(
        (v: any) => v[this.searchExpr].toLowerCase() === currentText.toLowerCase()
      );

      this.selectionChanged.emit({ selectedItem: findItem });
    } else {
      this.keyChanged.emit(this.targetSelectBox.text);
    }
  }

  get selectedDisplayText(): string {
    return this.targetSelectBox?.text ?? '';
  }

  get emptyMessage() {
    return this.hideEmptyResult ? ' ' : this.translateService.instant('string.noMatchFound');
  }

  input(event: any) {
    if (!this.hideEmptyResult) return;

    setTimeout(() => {
      const selectBoxElement = this.targetSelectBox.instance.$element()[0];

      const selectBoxInputId = selectBoxElement.getElementsByClassName('dx-texteditor-input-container')[0].firstChild
        .attributes['aria-controls'].value;
      const resultsContainer = document.getElementById(selectBoxInputId).parentElement.parentElement;

      const resultNodes = Array.from(
        document.getElementById(selectBoxInputId).children[0].children[0].children[0].children[1].children
      );

      const emptyMessageNode = resultNodes.find((node) => node.className === 'dx-empty-message');

      if (emptyMessageNode) resultsContainer.style.visibility = 'hidden';
      else resultsContainer.style.visibility = 'visible';
    }, 1000);
  }

  get hasError(): boolean {
    return !this.disabled && this.errors && this.touched;
  }

  get label() {
    return `${this.placeholder}${this.required ? ' *' : ''}`;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

    getDisplayValue(data: any, text?: string): string {
    if (typeof this.displayExpr === 'string') {
      return data[this.displayExpr];
    }
    if (typeof this.displayExpr === 'function') {
      return text ? this.displayExpr(data, text) : this.displayExpr(data);
    }
    return '';
  }
}
